import { getUserLocale } from "./number-format.util"

// The one place client-side code turns a Date into text for a human — the date
// sibling of number-format.util.js. Everything comes from the platform's
// Intl.DateTimeFormat for the BCP47 tag the page already exposes for numbers
// (meta[name="user-locale"]), so the browser's CLDR data decides separators
// and order and stays current without anyone editing a pattern here. The
// server side is Rails' `l` / `I18n.l` over config/locales/dates/<lang>.yml.
//
// Machine strings — an <input type="date"> value, a query parameter, a cache
// key — stay on toISODateString(); never feed them through formatDate.
//
// A date printed beside a server-rendered one replays the server's pattern
// instead (utils/server-format.util.js#formatDateWith). Digits here are
// always Latin, as the server prints them (Intl ar-SA would write ١٤:٠٥).

const FORMATS = {
  // Numeric, like date.formats.default on the server: 27. 11. 2024 (sk),
  // 27/11/2024 (fr), 11/27/2024 (en-US), 27.11.2024 (de, ro, uk).
  default: { day: "2-digit", month: "2-digit", year: "numeric" },
  // Spelled-out month: 27. novembra 2024 · 27 novembre 2024 · November 27, 2024.
  long: { day: "numeric", month: "long", year: "numeric" }
}
const TIME = { hour: "2-digit", minute: "2-digit", hourCycle: "h23" }

const pad = (n) => String(n).padStart(2, "0")

// "2026-08-31" is parsed as a LOCAL date: `new Date("2026-08-31")` is midnight
// UTC, which getDate() reads as the previous day west of Greenwich.
export function toDate(value) {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value
  if (value === null || value === undefined || value === "") return null
  if (typeof value === "string") {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/)
    if (match) return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12)
  }
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

// The inverse for machine use: a local calendar date as "YYYY-MM-DD".
export function toISODateString(value) {
  const date = toDate(value)
  if (!date) return ""
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

const format = (value, locale, options) => {
  const date = toDate(value)
  return date ? new Intl.DateTimeFormat(locale, { ...options, numberingSystem: "latn" }).format(date) : ""
}

// formatDate(value)                       → 27. 11. 2024 (sk-SK) · 27/11/2024 (fr-FR)
// formatDate(value, { format: "long" })   → 27. novembra 2024 · 27 novembre 2024
// formatDate(value, { locale: "fr-FR" })  → an explicit locale instead of the page's
// Returns "" for null, "" or an invalid date.
export function formatDate(value, { locale = getUserLocale(), format: name = "default" } = {}) {
  const options = FORMATS[name]
  if (!options) throw new RangeError(`unknown date format "${name}"`)
  return format(value, locale, options)
}

// The time of day alone, 24-hour clock: "14:05".
export function formatTime(value, { locale = getUserLocale() } = {}) {
  return format(value, locale, TIME)
}

// formatDateTime(value) → 27. 11. 2024 14:05 (sk-SK) · 27/11/2024 14:05 (fr-FR)
export function formatDateTime(value, { locale = getUserLocale() } = {}) {
  return format(value, locale, { ...FORMATS.default, ...TIME })
}
